import { FC, useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  createStyles,
  Fade,
  LinearProgress,
  Link,
  makeStyles,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { shuffle } from "lodash";
import { Link as RouterLink, useHistory } from "react-router-dom";

import { Button, Image } from "components";
import { CONTACT_US_FORM_URL } from "constants/index";
import * as routes from "routes";
import { useAssetKYWRoyaltyAccount } from "services/assets";
import { getLoadingMessage } from "services/loadingMessages";
import { LoadingMessage } from "services/loadingMessages/types";
import { getOrCreateValuation } from "services/valuation";
import { Valuation } from "services/valuation/types";
import { useValuationTask } from "services/valuationTask";
import theme from "theme";

const POLL_VALUATION_TASK_INTERVAL = 5000;
const DISPLAY_LOADING_MESSAGE_TIME = 20000;
const LOADING_MESSAGE_FADE_TIME = 1750;

const useStyles = makeStyles(() =>
  createStyles({
    title: {
      fontSize: "3rem",
      fontWeight: theme.typography.fontWeightLight,
      [theme.breakpoints.down("sm")]: {
        fontSize: "2.5rem",
      },
      [theme.breakpoints.down("xs")]: {
        fontSize: "2rem",
      },
    },
    responsiveVerticalSpacing: {
      marginTop: theme.spacing(8),
      [theme.breakpoints.down("sm")]: {
        marginTop: theme.spacing(4),
      },
      [theme.breakpoints.down("xs")]: {
        marginTop: theme.spacing(2),
      },
    },
    errorMessage: {
      fontSize: "1rem",
    },
    buttonsWrapper: {
      display: "flex",
      gap: `${theme.spacing(1)}px`,
      textAlign: "center",
      [theme.breakpoints.down("xs")]: {
        flexDirection: "column-reverse",
      },
    },
    loadingMessage: {
      fontWeight: theme.typography.fontWeightBold,
      marginTop: theme.spacing(1),
    },
    loadingMessageSource: {
      marginTop: theme.spacing(1),
    },
    progressBar: {
      backgroundColor: theme.custom.palette.gray.main,
      borderRadius: 5,
      height: 10,
      width: "500px",
      "& .MuiLinearProgress-bar": {
        backgroundColor: theme.custom.palette.green.main,
        borderRadius: 5,
      },
      [theme.breakpoints.down("xs")]: {
        width: "320px",
      },
    },
  }),
);

const ValuationLoading: FC = () => {
  const classes = useStyles();
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const history = useHistory();
  const assetId = new URLSearchParams(history.location.search).get("asset_id");
  const assetQueries = useAssetKYWRoyaltyAccount(assetId);
  const assetQueriesFailure = !!assetQueries.isError || !assetId;
  const { royalty_account } = assetQueries.data || {};
  const royaltyPayor = royalty_account?.royalty_payor;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [valuation, setValuation] = useState<Valuation>(null);
  const [isValuationRequested, setIsValuationRequested] = useState(false);
  useEffect(() => {
    const requestAndSetValuation = async () => {
      const valuation = await getOrCreateValuation(Number(assetId));
      setValuation(valuation);
    };
    // only request to create a valuation once we validate the asset id
    if (assetQueries.isSuccess && !isValuationRequested) {
      requestAndSetValuation();
      setIsValuationRequested(true);
    }
  }, [assetId, assetQueries, isValuationRequested]);

  const [isValuationComplete, setIsValuationComplete] = useState(false);
  const [error, setError] = useState(null);
  const valuationTaskQueries = useValuationTask(
    valuation?.valuation_task,
    !!valuation,
    isValuationComplete ? false : POLL_VALUATION_TASK_INTERVAL,
  );
  const { all_tasks_state, all_tasks_status_progress, all_tasks_error } =
    valuationTaskQueries.data || {};
  useEffect(() => {
    if (["completed", "failed"].includes(all_tasks_state)) {
      setIsValuationComplete(true);
      if (all_tasks_state === "failed") {
        setError(all_tasks_error);
      }
      if (all_tasks_state === "completed") {
        history.push(routes.sellerValuationResults(valuation.id.toString()));
      }
    }
  }, [
    all_tasks_state,
    all_tasks_status_progress,
    all_tasks_error,
    history,
    isValuationComplete,
    valuation?.id,
  ]);

  const [loadingMessages, setLoadingMessages] = useState<
    LoadingMessage[] | null
  >(null);
  const [currentMessageIndex, setCurrentMessageIndex] = useState<number>(0);
  const [fadeInMessage, setFadeInMessage] = useState(false);
  const currentMessage = useMemo(() => {
    return loadingMessages?.[currentMessageIndex];
  }, [currentMessageIndex, loadingMessages]);

  useEffect(() => {
    const initializeMessages = async () => {
      const messages = shuffle(await getLoadingMessage());
      setLoadingMessages(messages);
      setFadeInMessage(true);
    };
    initializeMessages();
  }, []);

  useEffect(() => {
    let current = true;
    let nextMessageTimer: NodeJS.Timeout;
    let fadeInMessageTimer: NodeJS.Timeout;
    if (!isValuationComplete && !!loadingMessages) {
      nextMessageTimer = setTimeout(() => {
        if (current) {
          setCurrentMessageIndex(prev => (prev + 1) % loadingMessages.length);
          setFadeInMessage(true);
        }
      }, DISPLAY_LOADING_MESSAGE_TIME);
      fadeInMessageTimer = setTimeout(() => {
        if (current) {
          setFadeInMessage(false);
        }
      }, DISPLAY_LOADING_MESSAGE_TIME - LOADING_MESSAGE_FADE_TIME);
    }
    return () => {
      current = false;
      clearTimeout(nextMessageTimer);
      clearTimeout(fadeInMessageTimer);
    };
  }, [currentMessageIndex, isValuationComplete, loadingMessages]);

  return (
    <Box
      height="100%"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      padding={isSmUp ? 6 : 2}
    >
      <Box width="100%" textAlign="center">
        <Image
          alt="Royalty Exchange Logo"
          name="orderbook/royalty_exchange_logo.svg"
        />
      </Box>
      {assetQueriesFailure && (
        <Box
          minHeight="160px"
          maxWidth="500px"
          mt={16}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="space-between"
        >
          <Alert
            severity="warning"
            icon={false}
            className={classes.errorMessage}
          >
            We couldn&apos;t find that account!
          </Alert>
          <Button
            component={RouterLink}
            to={routes.sellerDashboard()}
            variant="contained"
          >
            Back to My Royalties
          </Button>
        </Box>
      )}
      {!!error && (
        <>
          <Box maxWidth="500px" mt={8}>
            <Alert
              severity="warning"
              icon={false}
              className={classes.errorMessage}
            >
              <AlertTitle style={{ textAlign: "center" }}>
                {error.message}
              </AlertTitle>
              {!error.fix_instructions && error.description && (
                <Typography align="center">{error.description}</Typography>
              )}
              {error.fix_instructions && (
                <Typography align="center">{error.fix_instructions}</Typography>
              )}
            </Alert>
          </Box>
          <Box mt={8} className={classes.buttonsWrapper}>
            <Button
              component={RouterLink}
              to={routes.sellerDashboard()}
              variant="outlined"
            >
              Back to My Royalties
            </Button>
            {royaltyPayor.portal_url && error.royalty_payor_action_required && (
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={royaltyPayor.portal_url}
              >
                <Button variant="contained">
                  Go to {royaltyPayor.display_name} Portal
                </Button>
              </Link>
            )}
            {(error.message.includes("contact us") ||
              error.description.includes("contact us")) && (
              <Link
                target="_blank"
                rel="noopener noreferrer"
                href={CONTACT_US_FORM_URL}
              >
                <Button variant="contained">Contact Us</Button>
              </Link>
            )}
            {error.edit_credentials_required && (
              <Button
                component={RouterLink}
                to={`${routes.sellerDashboard()}?edit_asset_id=${assetId}`}
                variant="contained"
              >
                Update Credentials
              </Button>
            )}
          </Box>
        </>
      )}
      {assetQueries.isLoading && <CircularProgress />}
      {!isValuationComplete && !assetQueries.isLoading && !assetQueriesFailure && (
        <>
          <Fade in={!!royaltyPayor}>
            <Box className={classes.responsiveVerticalSpacing}>
              <Typography className={classes.title} variant="h1" align="center">
                Analyzing Your {royaltyPayor.display_name} Statements
              </Typography>
            </Box>
          </Fade>
          <Box
            height="250px"
            width="250px"
            className={classes.responsiveVerticalSpacing}
          >
            <Image alt="Record Player" name="orderbook/RecordPlayer.gif" />
          </Box>
          <Box
            maxWidth="500px"
            display="flex"
            flexDirection="column"
            alignItems="center"
          >
            <Box height="12px" className={classes.responsiveVerticalSpacing}>
              <LinearProgress
                className={classes.progressBar}
                variant="determinate"
                value={(all_tasks_status_progress ?? 0) * 100}
              />
            </Box>
            <Box height="100px">
              {!!currentMessage && (
                <Fade in={fadeInMessage} timeout={LOADING_MESSAGE_FADE_TIME}>
                  <div>
                    <Typography
                      align="center"
                      className={classes.loadingMessage}
                    >
                      {currentMessage.message}
                    </Typography>
                    {currentMessage.source && (
                      <Typography
                        align="center"
                        className={classes.loadingMessageSource}
                      >
                        –{currentMessage.source}
                      </Typography>
                    )}
                  </div>
                </Fade>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ValuationLoading;
